import { supabase } from '@/integrations/supabase/client';
import { NutritionService } from './NutritionService';

export interface WorkoutNutritionRecommendation {
  id: string;
  type: 'pre_workout' | 'post_workout' | 'recovery' | 'performance';
  message: string;
  priority: 'low' | 'medium' | 'high';
  actionable: boolean;
  timing?: string;
  nutritionTarget?: {
    calories?: number;
    protein?: number;
    carbs?: number;
    timing: string;
  };
}

export interface PerformanceCorrelation {
  date: string;
  nutritionScore: number;
  performanceScore: number;
  correlation: 'positive' | 'negative' | 'neutral';
  insights: string[];
}

export interface NetCalorieData {
  date: string;
  caloriesConsumed: number;
  caloriesBurned: number;
  netCalories: number;
  status: 'deficit' | 'surplus' | 'maintenance';
  recommendation: string;
}

export class NutritionActivityCorrelationService {
  static async getUpcomingWorkoutRecommendations(userId: string): Promise<WorkoutNutritionRecommendation[]> {
    const recommendations: WorkoutNutritionRecommendation[] = [];
    
    // Get upcoming workouts (scheduled for tomorrow)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];

    // Check for scheduled running activities
    const { data: upcomingRuns } = await supabase
      .from('running_performance')
      .select('*')
      .eq('user_id', userId)
      .eq('recorded_date', tomorrowStr);

    // Check for scheduled fitness activities
    const { data: upcomingFitness } = await supabase
      .from('general_fitness')
      .select('*')
      .eq('user_id', userId)
      .eq('recorded_date', tomorrowStr);

    // Get today's nutrition summary
    const todayStr = new Date().toISOString().split('T')[0];
    const todayNutrition = await NutritionService.getDailySummary(userId, todayStr);
    const nutritionData = todayNutrition?.data;

    if (upcomingRuns && upcomingRuns.length > 0) {
      for (const run of upcomingRuns) {
        if (run.distance_meters && run.distance_meters > 10000) { // Long run
          recommendations.push({
            id: `pre-long-run-${run.id}`,
            type: 'pre_workout',
            message: `You have a ${(run.distance_meters / 1000).toFixed(1)}km run tomorrow. Consider increasing carbs tonight for optimal fuel.`,
            priority: 'high',
            actionable: true,
            timing: 'tonight',
            nutritionTarget: {
              carbs: Math.round(run.distance_meters / 1000 * 8), // 8g carbs per km
              timing: '2-3 hours before bed'
            }
          });
        }

        if (run.run_type === 'interval' || run.run_type === 'tempo') {
          recommendations.push({
            id: `pre-intensity-${run.id}`,
            type: 'pre_workout',
            message: `High-intensity ${run.run_type} session tomorrow. Ensure adequate carbs 2-3 hours before.`,
            priority: 'medium',
            actionable: true,
            timing: 'pre-workout',
            nutritionTarget: {
              carbs: 30,
              timing: '2-3 hours before workout'
            }
          });
        }
      }
    }

    if (upcomingFitness && upcomingFitness.length > 0) {
      for (const activity of upcomingFitness) {
        if (activity.activity_type === 'weightlifting' || activity.activity_type === 'crossfit') {
          recommendations.push({
            id: `pre-strength-${activity.id}`,
            type: 'pre_workout',
            message: `Strength training tomorrow. Focus on protein today for muscle preparation.`,
            priority: 'medium',
            actionable: true,
            timing: 'today',
            nutritionTarget: {
              protein: 25,
              timing: 'with each meal'
            }
          });
        }
      }
    }

    // Recovery recommendations based on recent training load
    const { data: recentLoad } = await supabase
      .from('training_load_history')
      .select('*')
      .eq('user_id', userId)
      .gte('date', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
      .order('date', { ascending: false })
      .limit(3);

    if (recentLoad && recentLoad.length > 0) {
      const avgRecoveryScore = recentLoad.reduce((sum, day) => sum + (day.recovery_score || 100), 0) / recentLoad.length;
      
      if (avgRecoveryScore < 70) {
        recommendations.push({
          id: 'recovery-nutrition',
          type: 'recovery',
          message: `Your recovery score is ${avgRecoveryScore.toFixed(0)}%. Focus on protein and anti-inflammatory foods.`,
          priority: 'high',
          actionable: true,
          nutritionTarget: {
            protein: Math.round((nutritionData?.goal_protein || 120) * 1.2),
            timing: 'spread throughout the day'
          }
        });
      }
    }

    return recommendations;
  }

  static async getPerformanceCorrelations(userId: string, days: number = 30): Promise<PerformanceCorrelation[]> {
    const correlations: PerformanceCorrelation[] = [];
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    // Get nutrition summaries
    const { data: nutritionData } = await supabase
      .from('nutrition_daily_summary')
      .select('*')
      .eq('user_id', userId)
      .gte('date', startDate)
      .order('date', { ascending: true });

    // Get performance data
    const { data: performanceData } = await supabase
      .from('running_performance')
      .select('recorded_date, pace_per_km_seconds, trimp_score, hr_efficiency_score')
      .eq('user_id', userId)
      .gte('recorded_date', startDate)
      .order('recorded_date', { ascending: true });

    if (!nutritionData || !performanceData) return correlations;

    // Create date-based correlations
    const nutritionByDate = new Map(nutritionData.map(n => [n.date, n]));
    const performanceByDate = new Map();

    // Group performance data by date
    performanceData.forEach(p => {
      const date = p.recorded_date;
      if (!performanceByDate.has(date)) {
        performanceByDate.set(date, []);
      }
      performanceByDate.get(date).push(p);
    });

    performanceByDate.forEach((performances, date) => {
      const nutrition = nutritionByDate.get(date);
      if (!nutrition) return;

      // Calculate nutrition score (0-100)
      const proteinScore = Math.min(100, (nutrition.total_protein / (nutrition.goal_protein || 100)) * 100);
      const calorieScore = Math.min(100, Math.abs(100 - Math.abs((nutrition.total_calories / (nutrition.goal_calories || 2000) - 1) * 100)));
      const nutritionScore = (proteinScore + calorieScore) / 2;

      // Calculate performance score (0-100, higher pace score = better performance)
      const avgPace = performances.reduce((sum, p) => sum + (p.pace_per_km_seconds || 0), 0) / performances.length;
      const avgEfficiency = performances.reduce((sum, p) => sum + (p.hr_efficiency_score || 50), 0) / performances.length;
      const performanceScore = avgEfficiency; // Use HR efficiency as main performance indicator

      // Determine correlation
      let correlation: 'positive' | 'negative' | 'neutral' = 'neutral';
      const insights: string[] = [];

      if (nutritionScore > 80 && performanceScore > 70) {
        correlation = 'positive';
        insights.push('Strong nutrition compliance coincided with good performance');
      } else if (nutritionScore < 60 && performanceScore < 50) {
        correlation = 'negative';
        insights.push('Poor nutrition may have impacted performance');
      }

      if (nutrition.total_protein > (nutrition.goal_protein || 100) * 1.2) {
        insights.push('High protein intake on this day');
      }

      if (nutrition.net_calories < -500) {
        insights.push('Large caloric deficit may affect energy levels');
      }

      correlations.push({
        date,
        nutritionScore: Math.round(nutritionScore),
        performanceScore: Math.round(performanceScore),
        correlation,
        insights
      });
    });

    return correlations.slice(-14); // Return last 2 weeks
  }

  static async getNetCalorieAnalysis(userId: string, days: number = 7): Promise<NetCalorieData[]> {
    const results: NetCalorieData[] = [];
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000);

    for (let i = 0; i < days; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];

      const netCaloriesResponse = await NutritionService.calculateNetCalories(userId, dateStr);
      const dailySummary = await NutritionService.getDailySummary(userId, dateStr);

      if (dailySummary?.data && netCaloriesResponse?.data) {
        const netCalories = netCaloriesResponse.data.net;
        const status = netCalories > 200 ? 'surplus' : netCalories < -200 ? 'deficit' : 'maintenance';
        
        let recommendation = '';
        if (status === 'deficit' && netCalories < -500) {
          recommendation = 'Large deficit - consider increasing intake for energy';
        } else if (status === 'surplus' && netCalories > 500) {
          recommendation = 'Caloric surplus - good for muscle building goals';
        } else {
          recommendation = 'Balanced energy intake for current activity level';
        }

        results.push({
          date: dateStr,
          caloriesConsumed: dailySummary.data.total_calories || 0,
          caloriesBurned: dailySummary.data.calories_burned || 0,
          netCalories,
          status,
          recommendation
        });
      }
    }

    return results;
  }

  static async generateAdaptiveRecommendations(userId: string): Promise<WorkoutNutritionRecommendation[]> {
    const recommendations: WorkoutNutritionRecommendation[] = [];

    // Get recent training load and heart rate data
    const { data: recentRuns } = await supabase
      .from('running_performance')
      .select('*')
      .eq('user_id', userId)
      .gte('recorded_date', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
      .order('recorded_date', { ascending: false })
      .limit(5);

    // Get recent nutrition compliance
    const { data: recentNutrition } = await supabase
      .from('nutrition_daily_summary')
      .select('*')
      .eq('user_id', userId)
      .gte('date', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
      .order('date', { ascending: false })
      .limit(7);

    if (recentRuns && recentRuns.length > 0) {
      const avgHR = recentRuns.reduce((sum, run) => sum + (run.heart_rate_avg || 0), 0) / recentRuns.length;
      const avgTRIMP = recentRuns.reduce((sum, run) => sum + (run.trimp_score || 0), 0) / recentRuns.length;

      if (avgTRIMP > 200) { // High training load
        recommendations.push({
          id: 'high-load-recovery',
          type: 'recovery',
          message: `Your TRIMP score is high (${avgTRIMP.toFixed(0)}). Focus on protein and sleep quality for optimal recovery.`,
          priority: 'high',
          actionable: true,
          nutritionTarget: {
            protein: 150,
            timing: 'within 30 minutes post-workout and before bed'
          }
        });
      }

      if (avgHR > 0) {
        const maxHR = 220 - 30; // Approximate, could get from user profile
        const hrIntensity = (avgHR / maxHR) * 100;
        
        if (hrIntensity > 85) {
          recommendations.push({
            id: 'high-intensity-nutrition',
            type: 'performance',
            message: `High-intensity training detected. Optimize carb timing for better performance.`,
            priority: 'medium',
            actionable: true,
            nutritionTarget: {
              carbs: 40,
              timing: '1-2 hours pre-workout'
            }
          });
        }
      }
    }

    if (recentNutrition && recentNutrition.length > 0) {
      const avgCompliance = recentNutrition.reduce((sum, day) => {
        const proteinCompliance = day.goal_protein ? (day.total_protein / day.goal_protein) : 1;
        const calorieCompliance = day.goal_calories ? Math.abs(1 - Math.abs(day.total_calories / day.goal_calories - 1)) : 1;
        return sum + ((proteinCompliance + calorieCompliance) / 2);
      }, 0) / recentNutrition.length;

      if (avgCompliance > 0.9) {
        recommendations.push({
          id: 'nutrition-performance-boost',
          type: 'performance',
          message: `Excellent nutrition adherence (${(avgCompliance * 100).toFixed(0)}%)! Your consistent tracking is improving your pace.`,
          priority: 'low',
          actionable: false
        });
      } else if (avgCompliance < 0.7) {
        recommendations.push({
          id: 'improve-compliance',
          type: 'performance',
          message: `Nutrition compliance could improve (${(avgCompliance * 100).toFixed(0)}%). Better tracking can enhance your performance.`,
          priority: 'medium',
          actionable: true
        });
      }
    }

    return recommendations;
  }
}